import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { DetectMethodChanges, getShortUUID, isValidNumber, isVoid } from '../../utils';
import { MessageBusService } from '../../message-bus.service';
import { ShellClientService } from '../../shell-communication/shell-client.service';

import {
    AdjustInputs,
    CloseInputs,
    Holding,
    Leg,
    PackageCard,
    parseLegActionType,
    Run,
    RunOutcome,
    Solution,
    SolutionContent,
    TabKind
} from './copilot-domain';
import {
    applySolution,
    applySolutionAdditive,
    BackendOutcome,
    CoPilotSession,
    unApplySolution,
    unApplySolutionAdditive
} from './copilot-session';
import { closeRefusalText, toCloseSolutionContent } from './copilot-close-mapper';
import {
    CancelCoPilotRun,
    COPILOT_CONTRACT_VERSION,
    COPILOT_SOLUTIONS_READY_MESSAGE_TYPE,
    CoPilotCardDto,
    CoPilotHoldingDto,
    CoPilotLegDto,
    CoPilotPositionDto,
    CoPilotSolutionDto,
    CoPilotSolutionsReadyDto,
    EsContractGapDto,
    HM_CLOSE_CONTRACT_VERSION,
    HM_CLOSE_SOLUTIONS_READY_MESSAGE_TYPE,
    HmCloseSolutionsReadyDto,
    RunCoPilotSkill,
    RunHedgeMatrixCloseSkill
} from './copilot-operations';
import { mapAdjustInputs, mapCloseInputs, toCalendarDateString } from './copilot-mapper';
import {
    computeComboNet,
    isTooExpensive,
    ledgerPrice,
    minProfitAsPoints,
    RiskBand,
    riskBandOf,
    riskBandsPresent,
    SortDirection,
    SortKeys,
    sortedByKeys
} from './copilot-pricing';
import { LastQuoteCacheService } from '../../last-quote-cache.service';
import { QuoteDto } from '../../shell-communication/dtos/quote-dto.class';
import { ApplyResult, CoPilotBookHedge, IHedgingGridCopilotFacade } from './copilot-facade';
import { HmSelectCopilotFacade, ISelectCopilotHost } from './hm-select-copilot-facade';
import { OpgCopilotComponent } from '../../copilot-shared/opg-copilot.component';
import { HorizontalSkewService } from '../../horizontal-skew/horizontal-skew.service';
import { TradingInstrument } from '../../trading-instruments/trading-instrument.class';
import { OptionExpirationDescriptor } from '../../shell-communication/shell-dto-protocol';
import { CloseAllState, TabState } from './copilot-ui-types';
import {
    cloneAdjustInputs,
    cloneCloseInputs,
    diffAdjustInputs,
    diffCloseInputs,
    hasAnyChange,
    ParamChangeMap
} from './hg-copilot-param-diff';
import { parseOptionTicker } from '../../options-common/options.model';
import { HEDGE_BOOK_RESET_TOPIC, HedgeBookResetPayload } from '../hg-hedge-book-reset.message';
import { AdjustmentAvailabilityState } from './adjustment-availability-state';

// ~210s frontend backstop (the worst-case Run is ~187s; ADR-027/030). If no ESS push arrives, the
// Run is marked terminalFailed locally so the UI never hangs.
const FE_BACKSTOP_MS = 210000;

// CoPilot child element (CR-20260619-001, M2). Orchestrates the FE-only session: 3 questionnaire
// tabs, the RUN lifecycle (single-flight; async ESS result correlated by runId), Solution cards,
// and teardown/re-run. Grid effects (Preview/un-Preview, positions, book reads) go through the
// @Input() ctx facade (IHedgingGridCopilotFacade) — IMPLEMENTED + WIRED in M3; until then the child
// runs its full UI/logic and guards the grid-touching paths.
@Component({
    selector: 'ets-hg-copilot',
    templateUrl: 'hg-copilot.component.html',
    styleUrls: ['./hg-copilot.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    // The embedded Select Copilot (CR-20260804-001, ruling AF) injects HorizontalSkewService
    // through the hierarchical injector — in OPG the PANEL provides it; here this component
    // does, so the copilot resolves an instance driven by refreshStrikeContext below.
    providers: [HorizontalSkewService]
})
export class HgCopilotComponent implements OnInit, AfterViewInit, OnDestroy {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService,
        private readonly _toastr: ToastrService,
        private readonly _zone: NgZone,
        private readonly _horizontalSkew: HorizontalSkewService,
        private readonly _lastQuoteCache: LastQuoteCacheService
    ) {}

    private readonly _unsub = new Subject<void>();
    private readonly _session = new CoPilotSession(getShortUUID, () => Date.now());
    private _backstopHandle: any = null;
    private _initialized = false;
    private _portfolioId: string | null = null;

    // ---- stale-snapshot indicator (CR: refresh button turns yellow when the live Hedge Matrix has
    // diverged from what CoPilot last synced) ----
    // A deterministic fingerprint of the matrix reads CoPilot consumes is captured at every
    // CoPilot<->matrix sync point (conversation start / run / Preview). The refresh button goes yellow
    // when the live fingerprint differs from this baseline => only USER edits between syncs trip it.
    private _matrixSyncSignature: string | null = null;
    private _suppressStaleCheck = false;   // true while CoPilot's OWN matrix write is settling
    private readonly _emptyMatrixSignature = JSON.stringify({ p: [], h: [] });

    // The grid seam (ADR-025). Wired in M3; null until then.
    @Input() ctx: IHedgingGridCopilotFacade | null = null;
    @Input() portfolioOwnerId: string;
    @Input() adjustmentAvailability: AdjustmentAvailabilityState;
    private _availableTargetItemId: string;
    @Input() set availableTargetItemId(value: string) {
        this._availableTargetItemId = value;
        if (value) { this.activeTab = 'adjust'; this.additionalAdjustExpanded = true; }
    }
    get availableTargetItemId(): string { return this._availableTargetItemId; }
    @Input() set availableTargetRequest(value: number) {
        this.additionalTargetRequest = value;
        if (this._availableTargetItemId) { this.activeTab = 'adjust'; this.additionalAdjustExpanded = true; }
    }
    additionalTargetRequest = 0;
    @Input() popupVisible = false;
    additionalAdjustExpanded = false;
    private _additionalPreviewActive = false;
    private _additionalWriting = false;

    @DetectMethodChanges()
    onAdditionalExpanded(expanded: boolean): void {
        this.additionalAdjustExpanded = expanded;
        if (expanded) { this.activeTab = 'adjust'; }
    }
    @DetectMethodChanges()
    onAdditionalPreview(active: boolean): void {
        this._additionalPreviewActive = active;
        this._emitPreviewActive();
        this.captureMatrixSync();
        this._emitStale();
    }
    @DetectMethodChanges()
    onAdditionalMatrixMutation(writing: boolean): void {
        this._additionalWriting = writing;
        if (!writing) { this.captureMatrixSync(); this._emitStale(); }
    }

    // ---- ES mode (CR-20260924-002) ------------------------------------------------------------
    //
    // The host owns the toggle (title bar); this component follows it. The theme hook: the SCSS
    // turns every accent green under :host(.es-mode) — the embedded Select copilot included. The
    // book this component sees follows the toggle too (ON = ES hedges only, OFF = SPX hedges only,
    // owner ruling 2026-09-24), applied at the READ seam below so the Close tab's list, the dates on
    // offer, the positions sent and the stale fingerprint can never disagree.
    @HostBinding('class.es-mode')
    get esMode(): boolean {
        return !!this.ctx && this.ctx.readOrionEsMode();
    }

    // "ES" = any hedge not on the portfolio's own asset — the only non-base underlyings the matrix
    // admits. A hedge with no underlying recorded reads as the base.
    private inEsToggleScope(underlying: string | null | undefined): boolean {
        const base = this.ctx ? this.ctx.readBaseAsset() : null;
        const isEs = !!underlying && !!base && underlying !== base;
        return isEs === this.esMode;
    }

    private readBookPositions(): CoPilotPositionDto[] {
        return (this.ctx ? this.ctx.readWorkingStatePositions() : []).filter(p => this.inEsToggleScope(p.underlying));
    }

    // The toggle flipped: re-theme, re-read the date block (the embedded copilot re-syncs on the
    // same symbol) and let the stale cue follow — the fingerprint carries the mode.
    @DetectMethodChanges()
    private onOrionEsModeChanged(): void {
        if (this._selectCopilot) {
            this._selectCopilot.onOpened();
        }
        this._emitStale();
    }

    // ---- the embedded SELECT COPILOT (CR-20260804-001, the two-copilots reversal) -----------
    //
    // The Select tab hosts the LITERAL OPG Co-Pilot component; this section is the whole host
    // duty: the facade it talks through, the popup-shown/grid-changed/reset relays, and the
    // preview wrappers that reuse the Adjust cards' toast + stale-suppression discipline.
    // The embed lives under *ngIf="contentVisible", so a New Conversation / portfolio switch
    // RECREATES it — a fresh copilot conversation by construction; onOpened() is then called
    // on the fresh instance below.

    @ViewChild(OpgCopilotComponent)
    private _selectCopilot: OpgCopilotComponent | undefined;

    readonly selectFacade: HmSelectCopilotFacade = new HmSelectCopilotFacade(this.makeSelectHost());

    // The embedded copilot's width tier, re-emitted to the popup owner. THREE states, because
    // the Select tab is WIDER than the host's own tabs (owner smoke ruling 2026-08-04: +20 px
    // so the "Follow-Through" / "Expected Range" pills stop wrapping): 'wide' = Select,
    // Calls & Puts; 'normal' = Select, single side; 'none' = not on the Select tab.
    @Output() readonly widthModeChange = new EventEmitter<'none' | 'normal' | 'wide'>();

    private _embeddedStale = false;

    // CR-20260831-004 (defect round 1): the embedded copilot's GRID-DRIFT-ONLY cue — feeds the
    // header refresh button, which must ignore param drift (owner ruling: that button reports
    // underlying/book changes only). _embeddedStale above stays the UNION for the FAB.
    private _embeddedGridStale = false;

    // CR-20260831-004 round 3 (owner recording): the window-level LAST-RUN engine tab. When the
    // ACTIVE tab differs, the header tab bar paints the active segment yellow — "user is looking
    // not at the tab he ran previously". Adjust/Close runs are seen directly in onRun; a Select
    // run arrives via the embedded copilot's runFired output. null until a run; reset with the
    // conversation.
    private _lastRunEngineTab: TabKind | null = null;

    // CR-20260918-001: per tab, the working-state book as read when that tab's current run was fired.
    // The Solution cards hand it to the Transactions popup so an Adjust package's BEFORE column shows
    // what the target hedge held at Run time.
    private _heldAtRun: { [kind: string]: CoPilotPositionDto[] } = {};

    get heldAtRun(): CoPilotPositionDto[] {
        return this._heldAtRun[this.activeTab] || [];
    }

    get activeTabNotLastRun(): boolean {
        return this._lastRunEngineTab !== null && this.activeTab !== this._lastRunEngineTab;
    }

    @DetectMethodChanges()
    onEmbeddedRunFired(): void {
        this._lastRunEngineTab = 'select';
        this._emitStale();
    }

    // CR-20260901-001: the portfolio's default quantity for the Adjust tab's mode-flip value
    // conversion — a live facade read per CD (cheap; same source the Select host closure uses).
    get defaultQty(): number | undefined {
        return this.ctx ? this.ctx.readDefaultQty() : undefined;
    }

    private _hsInited = false;

    private makeSelectHost(): ISelectCopilotHost {
        return {
            entitled: () => !!this.ctx && this.ctx.isEntitled(),
            expirationDescriptors: () => this.ctx ? this.ctx.readAvailableExpirationDescriptors() : [],
            currentDateKey: () => this.selectCurrentDateKey,
            instrument: () => this.targetInstrument,
            underlying: () => this.underlyingTicker(),
            chosenAtm: () => this.ctx ? this.ctx.readChosenAtm() : null,
            dynamicAtm: () => this.ctx ? this.ctx.readDynamicAtm() : null,
            defaultQty: () => this.ctx ? this.ctx.readDefaultQty() : undefined,
            presetApgSource: () => {
                const portfolio = this.ctx && this.ctx.readPresetPortfolio ? this.ctx.readPresetPortfolio() : null;
                return portfolio ? JSON.stringify({ownerId: portfolio.userId, portfolioId: portfolio.id}) : null;
            },
            applyPreview: (id, legs, cards) => this.applySelectPreview(id, legs, cards),
            removePreview: (id) => this.removeSelectPreview(id),
            previewLost: (id) => this.onSelectPreviewLost(id),
            // CR-20260924-002 (ES mode) — live closures over the host, like the reads above.
            esMode: () => this.esMode,
            esContracts: () => this.ctx ? this.ctx.readEsContractCandidates() : null,
            esExpirationDescriptors: (contract) => this.ctx ? this.ctx.readEsExpirationDescriptors(contract) : [],
            matrixEsHedgingEnabled: () => !!this.ctx && this.ctx.readMatrixEsHedgingEnabled(),
            normFactor: (asset) => this.ctx ? this.ctx.getNormFactor(asset || '') : 1
        };
    }

    // The popup opened — relay to the embedded copilot (the OPG host calls onOpened() on every
    // open; conversation continuity vs fresh start is decided inside, keyed on the symbol).
    public onHostPopupShown(): void {
        if (this._selectCopilot) {
            this._selectCopilot.onOpened();
        }
    }

    @DetectMethodChanges()
    onSelectWidthMode(mode: 'normal' | 'wide'): void {
        // Width applies only while the Select tab is showing; a background copilot tab change
        // must not resize the window under the Adjust/Close tabs.
        if (this.activeTab === 'select') {
            this.widthModeChange.emit(mode);
        }
    }

    // The embedded copilot's own stale cue (its grid fingerprint: current date, chosen ATM,
    // default qty, symbol) joins the launcher yellow alongside the matrix-book staleness.
    @DetectMethodChanges()
    onEmbeddedStale(stale: boolean): void {
        this._embeddedStale = stale;
        this._emitStale();
    }

    // The grid-drift-only sibling (CR-20260831-004 defect round 1) — repaints the refresh button.
    @DetectMethodChanges()
    onEmbeddedGridStale(stale: boolean): void {
        this._embeddedGridStale = stale;
    }

    // Preview wrapper — the same discipline as onPreviewToggle: CoPilot's OWN matrix write is
    // stale-suppressed, re-baselined in the finally, and every refusal reason gets its toast.
    private async applySelectPreview(id: string, legs: Leg[], cards: PackageCard[]): Promise<ApplyResult> {
        if (!this.ctx) {
            this._toastr.info('Preview Becomes Available Once ORION is Connected to the Grid.');
            return { applied: false };
        }
        this._suppressStaleCheck = true;
        try {
            const result = await this.ctx.applySolution(id, legs, cards);
            if (!result.applied) {
                if (result.reason === 'hedge-locked') {
                    this._toastr.error('Cannot Preview: Another Previewed Solution is Already Using One of These Hedges. Un-Preview That One First.');
                } else {
                    this._toastr.warning('Could Not Preview This Trade: a Leg Could Not be Placed (Nothing Was Placed).');
                }
            }
            return result;
        } finally {
            this.captureMatrixSync();
            this._suppressStaleCheck = false;
            this._emitStale();
        }
    }

    private async removeSelectPreview(id: string): Promise<void> {
        if (!this.ctx) {
            return;
        }
        this._suppressStaleCheck = true;
        try {
            await this.ctx.removeCopilotLegs(id);
        } finally {
            this.captureMatrixSync();
            this._suppressStaleCheck = false;
            this._emitStale();
        }
    }

    // An async preview failed after loadTrade returned — revert the card's Previewed state
    // through the copilot's own consistency hook (the same path a grid-side removal takes).
    private onSelectPreviewLost(id: string): void {
        if (this._selectCopilot) {
            this._selectCopilot.onHostStrategyRemoved(id);
        }
        this._changeDetector.detectChanges();
    }

    private underlyingTicker(): string | null {
        const inst = this.targetInstrument;
        return inst ? (inst.underlying || inst.ticker || null) : null;
    }

    // The portfolio the grid has loaded. One Conversation per portfolio (FR-130); a change tears the
    // old one down (un-Preview all) and starts fresh (FR-110).
    @Input()
    set portfolioId(value: string | null) {
        if (value === this._portfolioId) {
            return;
        }
        this._portfolioId = value;
        if (this._initialized) {
            this.onPortfolioChanged();
        }
    }
    get portfolioId(): string | null {
        return this._portfolioId;
    }

    // The tab switcher is a dx-button-group (owner ruling 2026-06-22 — same segmented control as the
    // questionnaire controls below, replacing dx-tabs). Items carry the TabKind id so selection keys
    // directly on activeTab — no index<->kind round-trip.
    readonly tabItems: { id: TabKind; text: string }[] = [
        { id: 'select', text: 'Select' },
        { id: 'adjust', text: 'Adjust' },
        { id: 'close', text: 'Close' }
    ];

    activeTab: TabKind = 'select';
    contentVisible = true;   // toggled off/on to recreate questionnaires on new-conversation

    // ---- dynamic popup height (CR-20260619-001, owner 2026-06-23) ----
    // The host dx-popup auto-sizes to this child's content. One ResizeObserver on the scrollable content
    // column is the single structural trigger that covers every case the owner listed: Hedge-Pad settings
    // collapse, Solution card expand/collapse, solutions arriving after a Run, and tab switch. We emit the
    // natural content height; the host (HedgingGridComponent) owns the clamp + the popup geometry.
    @Output() readonly contentHeightChange = new EventEmitter<number>();

    // CR: emits whether ANY Solution (any tab) is currently previewed, so the host can turn the CoPilot
    // launcher green while a preview is in effect. The preview/session state is OWNED here (CoPilotSession);
    // the host only proxies grid mutations via the facade, so it cannot derive this itself — it must be
    // pushed up (mirrors contentHeightChange). Deduped so it fires only on an actual transition.
    @Output() readonly previewActiveChange = new EventEmitter<boolean>();
    private _lastEmittedPreviewActive = false;

    // CR: emits whether the live Hedge Matrix has DRIFTED from CoPilot's last sync (the same condition that
    // turns the in-popup refresh button yellow). Pushed up so the host can paint the COLLAPSED launcher
    // yellow/black — the in-popup indicator is invisible while the window is closed, so a trader who
    // manually edits the matrix while CoPilot is minimized would otherwise have no drift cue. Same rationale
    // as previewActiveChange (the stale/sync state is OWNED here), deduped so it fires only on a transition.
    @Output() readonly staleChange = new EventEmitter<boolean>();
    private _lastEmittedStale = false;

    private _scrollContentEl: HTMLElement | null = null;
    private _contentResizeObserver: ResizeObserver | null = null;
    private _contentHeightRafId: number | null = null;
    private _lastEmittedContentHeight = -1;

    // Re-attaches the observer whenever the scroll-content element appears/disappears (it lives under
    // *ngIf="contentVisible", which toggles on new-conversation).
    @ViewChild('scrollContent')
    set scrollContentRef(ref: ElementRef<HTMLElement> | undefined) {
        const el = ref ? ref.nativeElement : null;
        if (el === this._scrollContentEl) {
            return;
        }
        if (this._contentResizeObserver && this._scrollContentEl) {
            this._contentResizeObserver.unobserve(this._scrollContentEl);
        }
        this._scrollContentEl = el;
        if (el && this._contentResizeObserver) {
            this._contentResizeObserver.observe(el);
            this._scheduleContentMeasure();
        }
    }

    // Per-tab collapse (owner 2026-06-22): clicking Run collapses that tab's questionnaire to an input
    // summary, freeing space for solutions. Clicking the summary (or a non-success outcome) re-expands.
    // Reset to all-expanded on new-conversation / portfolio switch (a fresh form).
    collapsed: { select: boolean; adjust: boolean; close: boolean } = { select: false, adjust: false, close: false };

    // ---- Close-All aggregate (CR-20260619-001, Close-All increment) ----
    // Master state for the "Close All / Chosen" aggregate card (close tab only). Preview/Apply here act on
    // EVERY close Solution at once; while previewed, the per-hedge child cards are locked (aggregateLocked).
    // Reset on every Conversation boundary / re-run (a fresh close batch). Owned HERE (single source of
    // truth); the aggregate card reads it via @Input.
    closeAllState: CloseAllState = { previewed: false, applied: false };

    // Whether the aggregate "Close All / Chosen" card is expanded. Owned HERE so the individual per-hedge
    // cards can collapse to a one-line "{N} individual items" summary while it is open (frees space for the
    // aggregate detail) — the same collapse-to-summary pattern as the questionnaire/settings. Reset with the
    // batch (a fresh close run starts collapsed).
    closeAllExpanded = false;

    // No select member since the two-copilots reversal: the Select tab is the embedded Select
    // Copilot, which owns its whole questionnaire/run lifecycle — this state serves only the
    // tabs that still run through the HM wire.
    private _tabState: { adjust?: TabState<AdjustInputs>; close?: TabState<CloseInputs> } = {};

    // ---- parameter-change-vs-run cues (CR-20260831-004) -------------------------------------------
    // Per-tab flat maps of the questionnaire fields that no longer match what the tab's CURRENT run
    // sent (hg-copilot-param-diff key grammar). Empty while the tab has never run; re-emptied by a
    // new Run or New Conversation. FRESH objects per recompute — the OnPush questionnaire/summary
    // children wake on the reference. The Select tab's diff lives inside the embedded OPG copilot
    // and reaches this host only through its staleChange (onEmbeddedStale).
    adjustParamChanges: ParamChangeMap = {};
    closeParamChanges: ParamChangeMap = {};

    // Submitted inputs feeding each tab's collapsed summary (last emitted = what was Run).
    get adjustInputs(): AdjustInputs | null { return this._tabState.adjust ? this._tabState.adjust.inputs : null; }
    get closeInputs(): CloseInputs | null { return this._tabState.close ? this._tabState.close.inputs : null; }

    // Target mode of the last-Run close questionnaire — drives the aggregate "Close All / Chosen" title.
    get closeTargetKind(): 'all' | 'hedges' {
        const inputs = this.closeInputs;
        return inputs ? inputs.target.kind : 'all';
    }

    // True when the individual close cards should collapse to the "{N} individual items" summary line —
    // i.e. the close tab's aggregate card is present (>1 hedge) AND expanded.
    get individualsCollapsed(): boolean {
        return this.activeTab === 'close' && this.closeAllExpanded && this.activeSolutions.length > 1;
    }

    // ---- lifecycle ----

    ngOnInit(): void {
        this._initialized = true;
        this._session.startConversation(this._portfolioId || '');
        this.captureMatrixSync();   // initial baseline (adopted to the first populated read on the next matrix event)
        // Event-driven reactivity: the Hedge Matrix publishes Hg.HedgeMatrixChanged from its post-mutation
        // choke (recalculatePnls) on EVERY state change. Re-check the OnPush indicators on that real event
        // instead of polling the unreliable quote tick (silent outside market hours). debounceTime coalesces
        // the bursty recalc calls. The subscription is live even while the popup is hidden (component stays
        // mounted), so edits made while minimized update the indicator and bring-back is already correct.
        this._messageBus.of('Hg.HedgeMatrixChanged')
            .pipe(debounceTime(100), takeUntil(this._unsub))
            .subscribe(() => this.onMatrixChanged());
        this._messageBus
            .of<CoPilotSolutionsReadyDto>(COPILOT_SOLUTIONS_READY_MESSAGE_TYPE)
            .pipe(takeUntil(this._unsub))
            .subscribe(m => this.onSolutionsReady(m.payload));
        // CR-20260924-002: the ORION ES toggle (host title bar) — published on every flip and again
        // when a candidate's ES chain arrives, so the date block fills in as soon as it can.
        this._messageBus
            .of('Hg.OrionEsModeChanged')
            .pipe(takeUntil(this._unsub))
            .subscribe(() => this.onOrionEsModeChanged());
        // CR-20260910-001 milestone 2. THE CLOSE'S OWN MESSAGE, subscribed beside Adjust's rather
        // than folded into it: the Close is its own engine on the backend with its own delivery, so
        // an Adjust push and a Close push can arrive for the same panel and must not be mistaken
        // for one another.
        this._messageBus
            .of<HmCloseSolutionsReadyDto>(HM_CLOSE_SOLUTIONS_READY_MESSAGE_TYPE)
            .pipe(takeUntil(this._unsub))
            .subscribe(m => this.onCloseSolutionsReady(m.payload));
        // The hedge book was replaced or reset wholesale (hedges cleared, defaults loaded, grid rebuilt,
        // saved state cleared). Owner 2026-08-03: each of those "should have the same effect on the CoPilot
        // as the reset button does" — so this IS the reset button's handler, called by the same name. The
        // Conversation started against a book that no longer exists; keeping its results would leave the
        // trader with solutions computed for a vanished position. Not merely a re-baseline: a re-baseline
        // would silence the cue while the dead results stayed on the board.
        this._messageBus
            .of<HedgeBookResetPayload>(HEDGE_BOOK_RESET_TOPIC)
            .pipe(takeUntil(this._unsub))
            .subscribe(() => this.onNewConversation());
        // Keep targetInstrument current for the strike dropdowns: read it now + whenever the Hedge Matrix
        // re-resolves its strike context (portfolio change). The matrix owns that data and sets it async,
        // which the OnPush child cannot otherwise observe; without this push the strike dropdowns would
        // never enable. The matrix emits the signal only after its instrument + chain are built, so the
        // re-read here always finds the data present (race-free) — see hg-hedge-matrix onPortfolioSelected.
        this.refreshStrikeContext();
        this._messageBus
            .of('Hg.HedgeMatrixStrikeContextChanged')
            .pipe(takeUntil(this._unsub))
            .subscribe(() => this.refreshStrikeContext());
        // R9 (CR-20260805-001): the min-profit filter reads live mids, and this component is
        // OnPush - without a quote-driven CD pass the hidden set would only ever update when
        // something else happened to tick. The CARDS own the ticker subscriptions (a filtered
        // card is CSS-hidden, never destroyed, precisely so its ref-counted quotes keep
        // streaming and it can come back when it turns affordable); this listener only re-runs
        // the check. Same pattern as the card's own onQuoteTick.
        this._messageBus.of<QuoteDto[]>('QuoteDto')
            .pipe(takeUntil(this._unsub))
            .subscribe(() => this.onQuoteTick());
    }

    // Every 'QuoteDto' bus tick forces one CD pass (decorator); the R9 filter getters below then
    // re-pull fresh mids. Mirrors HgCopilotSolutionCardComponent.onQuoteTick.
    //
    // It is also the only place that can notice the list has become fully priced, which is the
    // condition the one-shot price sort waits for. Checking here rather than polling keeps the
    // sort tied to real price arrivals; the 10-second deadline covers the case where they stop.
    @DetectMethodChanges()
    onQuoteTick(): void {
        this.settlePriceSortIfReady();
    }

    ngAfterViewInit(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;   // older browsers: popup keeps its bound height, no auto-size (graceful degrade)
        }
        this._contentResizeObserver = new ResizeObserver(() => this._scheduleContentMeasure());
        if (this._scrollContentEl) {
            this._contentResizeObserver.observe(this._scrollContentEl);
            this._scheduleContentMeasure();
        }
    }

    ngOnDestroy(): void {
        // reload/logout teardown (FR-111/141): un-Preview everything; cancel any in-flight Run.
        this.clearBackstop();
        this.clearSortTimer();   // a pending price-sort deadline must not outlive the component
        this.cancelInFlightSilently();
        this.teardownAllPreviews();
        if (this._contentHeightRafId !== null) {
            cancelAnimationFrame(this._contentHeightRafId);
            this._contentHeightRafId = null;
        }
        if (this._contentResizeObserver) {
            this._contentResizeObserver.disconnect();
            this._contentResizeObserver = null;
        }
        this._unsub.next();
        this._unsub.complete();
    }

    // Coalesce ResizeObserver bursts (popup open animation, card expand transitions) into one measure per
    // frame, mirroring the price-chart host observer. _measureAndEmitContentHeight skips the hidden state
    // (the popup keeps its content mounted while closed => display:none => offsetHeight 0) and no-op repeats.
    private _scheduleContentMeasure(): void {
        if (this._contentHeightRafId !== null) {
            return;
        }
        this._contentHeightRafId = requestAnimationFrame(() => {
            this._contentHeightRafId = null;
            this._measureAndEmitContentHeight();
        });
    }

    private _measureAndEmitContentHeight(): void {
        const el = this._scrollContentEl;
        if (!el) {
            return;
        }
        const h = el.offsetHeight;
        if (h <= 0 || h === this._lastEmittedContentHeight) {
            return;   // hidden (kept-mounted popup) or unchanged
        }
        this._lastEmittedContentHeight = h;
        this._zone.run(() => this.contentHeightChange.emit(h));
    }

    // ---- tab navigation + questionnaire state ----

    @DetectMethodChanges()
    onTabClick(e: { itemData?: { id: TabKind } }): void {
        if (e && e.itemData) {
            this.activeTab = e.itemData.id;
            this._emitStale();   // round 3: the tab cue is part of the launcher union
            if (this.activeTab === 'select') {
                // Entering the Select tab: onOpened() re-syncs the embedded copilot's view and
                // re-emits its width mode (490/960), which onSelectWidthMode now lets through.
                if (this._selectCopilot) {
                    this._selectCopilot.onOpened();
                }
            } else {
                this.widthModeChange.emit('none');   // the other tabs keep the HM copilot's own width
            }
        }
    }

    // Click-to-expand: re-open a collapsed tab's full form to edit + re-run (owner 2026-06-22).
    @DetectMethodChanges()
    expandTab(kind: TabKind): void {
        this.collapsed[kind] = false;
    }

    // Manual collapse back to the input summary WITHOUT running (owner request 2026-08-04:
    // the Adjust tab gains the Select/OPG "Collapse Settings" affordance — collapse was a Run
    // side-effect only, expand had no way back). Mirrors the OPG orchestrator's
    // onCollapseRequested; purely presentational.
    @DetectMethodChanges()
    onCollapseAdjustRequested(): void {
        this.collapsed.adjust = true;
    }

    // "a run has completed on the Adjust tab" — gates the manual collapse hint, mirroring the
    // OPG rule exactly: a run exists once onRun creates it; 'pending' is moot (the form is
    // already collapsed then); every settled outcome — including 'empty' — counts.
    get hasCompletedAdjustRun(): boolean {
        const t = this._session.tab('adjust');
        const run = t ? t.currentRun : null;
        return !!run && run.outcome !== 'pending';
    }

    @DetectMethodChanges()
    onAdjustState(s: TabState<AdjustInputs>): void {
        this._tabState.adjust = s;
        // CR-20260831-004: every Adjust questionnaire edit funnels through here — re-diff vs the run.
        this._recomputeParamChanges();
    }

    @DetectMethodChanges()
    onCloseState(s: TabState<CloseInputs>): void {
        this._tabState.close = s;
        // CR-20260831-004: same for Close.
        this._recomputeParamChanges();
    }

    // CR-20260831-004: re-diff both runnable tabs' LIVE inputs against their current runs'
    // baselines. Called from every questionnaire edit, from Run fire (both sides equal => that
    // tab empties) and from New Conversation (baselines gone => both empty).
    private _recomputeParamChanges(): void {
        const adjustTab = this._session.tab('adjust');
        const adjustRun = adjustTab ? adjustTab.currentRun : null;
        this.adjustParamChanges = diffAdjustInputs(
            adjustRun ? adjustRun.sentInputs as AdjustInputs : null,
            this._tabState.adjust ? this._tabState.adjust.inputs : null);

        const closeTab = this._session.tab('close');
        const closeRun = closeTab ? closeTab.currentRun : null;
        this.closeParamChanges = diffCloseInputs(
            closeRun ? closeRun.sentInputs as CloseInputs : null,
            this._tabState.close ? this._tabState.close.inputs : null);

        this._emitStale();
    }

    // ---- derived view state ----

    get activeRun(): Run | null {
        const t = this._session.tab(this.activeTab);
        return t ? t.currentRun : null;
    }

    // ---- SORTING: ONCE WHEN THE PRICES SETTLE, AND AGAIN ONLY WHEN THE TRADER ASKS -----------
    //
    // The engine ships packages unpriced and in ITS order (exactness, then tidiness), so the list
    // the trader first sees can put a deep debit above a small credit — which is what the owner hit:
    // two $3.35 debits leading, and the answer he wanted sitting at #7 for pennies.
    //
    // ⚠ THE 2026-07-28 WITHDRAWAL STANDS, AND NEITHER THE SETTLE NOR THE NEW CONTROL REVERSES IT.
    // Live price-sorting was tried that day and pulled the same day: quotes stream in one at a time,
    // so every arriving mid re-ranked the list and cards jumped under the cursor. The note left
    // behind said to re-introduce sorting "only behind something that settles first, never live off
    // the quote stream". The settle is that something: the list is ordered when every leg of every
    // package has a price (or the 10-second deadline expires), and NO quote arrival ever re-orders
    // it afterwards. CR-20260824-003 adds exactly one other trigger — a CLICK on the Sort by
    // control — which is the trader's own deliberate act, not the quote stream moving cards under
    // their cursor. That distinction is the whole of the rule, and it is why `_sortKeys` is read at
    // ordering time rather than a second, competing order being kept alongside the frozen one.
    //
    // Default order: risk first (Aggressive, Moderate, Low), then best price, credits above debits
    // (owner, CR-20260824-003). On any run that is not fanned out over "Max Risk: All" no package
    // carries a band, the risk key finds nothing to separate, and the result is the pre-CR order —
    // which is why this change is invisible until the trader runs All or clicks something.

    /// The 10-second deadline. A quote that has not arrived by then is not going to settle the list
    /// on its own — outside market hours it may never arrive at all — and a list that never sorts is
    /// worse than one sorted on what is known.
    private static readonly PriceSortDeadlineMs = 10000;

    private _sortRunId: string | null = null;
    private _sortWaiting = false;
    /// The frozen order per RUN — see activeSolutions for why it is keyed rather than a single slot.
    /// Pruned to the three tabs' current runs on every write, so it cannot grow with the session.
    private _sortedIds: { [runId: string]: string[] } = {};
    private _sortTimer: any = null;

    // ---- THE SORT BY CONTROL (owner screencast, 2026-08-24) ---------------------------------
    //
    // Two keys, STACKING rather than mutually exclusive, with risk as the OUTER one whatever order
    // they were clicked in (owner's choice): packages group into risk bands, and inside each band
    // they run best price first. Each key cycles desc -> asc -> none, so 'none' on both hands the
    // list back to the engine's order.
    //
    // ⚠ NOT PERSISTED, and it survives a new run — the same standing-choice reasoning the retired
    // cost filter carried: a sort the trader chose is a reading preference they set on purpose, and
    // silently reverting it on the next Run is its own kind of surprise. Nothing under copilot/
    // persists per-user UI state today.
    private _sortKeys: SortKeys = { risk: 'desc', price: 'desc' };

    get sortKeys(): SortKeys {
        return this._sortKeys;
    }

    /// Risk is offered ONLY when it can actually reorder something: two or more distinct bands in
    /// THIS tab's result set. A "Max Risk: All" run whose other levels were refused comes back with
    /// one band — and since 2026-08-24 the panel does not say so in words either, so the disabled
    /// state and its title are the only account of it. Enabling a control that provably cannot move
    /// a card is worse than showing it disabled: the trader clicks it and learns nothing.
    /// Always false on Close (locally-built closes carry no band) and on any single-level Adjust run.
    get riskSortAvailable(): boolean {
        const sols = this.engineOrderedSolutions;
        return riskBandsPresent(sols.map(s => s.content ? s.content.displayName : undefined)).length > 1;
    }

    /// The arrow. No glyph for 'none' — an absent arrow IS the state, and a third glyph for "not
    /// sorting by this" reads as a third sort mode. A blocked Risk key shows none either: its
    /// stored direction is still 'desc' (it keeps its place for when a fanned-out run arrives),
    /// and painting an arrow over a control that cannot act would be the panel claiming an order
    /// it is not applying. Answering null rather than gating in the template keeps that rule in
    /// ONE place — the template holds the arrow's slot unconditionally, so nothing shifts.
    sortArrowClass(key: 'risk' | 'price'): string | null {
        if (key === 'risk' && !this.riskSortAvailable) {
            return null;
        }
        const dir = this._sortKeys[key];
        if (dir === 'desc') {
            return 'fas fa-caret-down';
        }
        if (dir === 'asc') {
            return 'fas fa-caret-up';
        }
        return null;
    }

    /// desc -> asc -> none -> desc. Starting from the default (desc), one click gives the reverse
    /// reading, a second takes the key out, a third puts it back.
    @DetectMethodChanges()
    onSortKeyClick(key: 'risk' | 'price'): void {
        if (key === 'risk' && !this.riskSortAvailable) {
            return;   // the disabled control is inert, not silently active
        }
        const next: { [dir: string]: SortDirection } = { desc: 'asc', asc: 'none', none: 'desc' };
        this._sortKeys = {
            risk: key === 'risk' ? next[this._sortKeys.risk] : this._sortKeys.risk,
            price: key === 'price' ? next[this._sortKeys.price] : this._sortKeys.price
        };

        // Re-order NOW, on whatever prices are known. Deliberately does NOT clear `_sortWaiting`:
        // a run still streaming quotes must keep its pending settle, which will re-apply these same
        // keys once every leg has priced. Cancelling it here would leave the list permanently
        // ordered on a partial price set because the trader happened to click early.
        this.applySortOrder();
    }

    /// The engine's own order, before any price sorting — the input to the sort and the fallback
    /// while it is still waiting.
    private get engineOrderedSolutions(): Solution[] {
        const t = this._session.tab(this.activeTab);
        return t ? t.solutions : [];
    }

    get activeSolutions(): Solution[] {
        const sols = this.engineOrderedSolutions;
        const run = this.activeRun;

        // A frozen order belongs to ONE run, and is looked up BY that run. It used to be a single
        // slot holding the newest run's order, which was fine while only the settle wrote it: a
        // different tab's run simply found a mismatch and fell back to the engine's order.
        // CR-20260824-003 made the order clickable, and a control that does nothing on the tab in
        // front of the trader is a defect rather than a fallback — so each run keeps its own.
        const frozen = run ? this._sortedIds[run.id] : undefined;
        if (!frozen) {
            return sols;
        }

        const rank: { [id: string]: number } = {};
        for (let i = 0; i < frozen.length; i++) {
            rank[frozen[i]] = i;
        }
        // A solution the frozen order does not know keeps the end rather than being dropped.
        const at = (s: Solution) => (rank[s.id] === undefined ? Number.MAX_SAFE_INTEGER : rank[s.id]);
        return sols.slice().sort((a, b) => at(a) - at(b));
    }

    /// True while the panel is holding the list unsorted, waiting for prices. Drives the indicator.
    get isSettlingPrices(): boolean {
        const run = this.activeRun;
        return this._sortWaiting && !!run && run.id === this._sortRunId;
    }

    /// Start waiting for this run's prices. Called when results arrive, never from a getter.
    private armPriceSort(runId: string): void {
        this.clearSortTimer();
        this._sortRunId = runId;
        delete this._sortedIds[runId];   // a fresh run starts from the engine's order, never a stale one
        this._sortWaiting = true;

        this._sortTimer = setTimeout(() => {
            // DEADLINE. Sort on what is known; anything still unpriced goes last, because an
            // unpriced package cannot be ranked against a priced one and guessing a price for it
            // is worse than admitting it has none.
            this._sortTimer = null;
            if (this._sortWaiting && this._sortRunId === runId) {
                this.freezePriceOrder();
            }
        }, HgCopilotComponent.PriceSortDeadlineMs);
    }

    private clearSortTimer(): void {
        if (this._sortTimer !== null) {
            clearTimeout(this._sortTimer);
            this._sortTimer = null;
        }
    }

    /// Sort when EVERY package has a price — the whole point of waiting. A partially-priced list
    /// sorted early is the jumping list of 2026-07-28 with extra steps.
    private settlePriceSortIfReady(): void {
        const run = this.activeRun;
        if (!this._sortWaiting || !run || run.id !== this._sortRunId) {
            return;
        }
        const sols = this.engineOrderedSolutions;
        if (sols.length === 0) {
            return;
        }
        for (let i = 0; i < sols.length; i++) {
            if (this.solutionLedgerPrice(sols[i]) === undefined) {
                return;   // still streaming
            }
        }
        this.freezePriceOrder();
    }

    /// Take the order and keep it. Frozen as IDS rather than as a list, so a later re-render
    /// reading fresh solution objects still lands in the same places.
    ///
    /// TWO CALLERS, ONE ORDERING. The price settle below and a Sort by click both land here, so
    /// there is exactly one place that decides what "better" means and no chance of the control and
    /// the settle disagreeing about it.
    ///
    /// ⚠ NOT decorated with @DetectMethodChanges - BOTH callers are, and the decorator runs
    /// detectChanges when the method it wraps RETURNS. Decorating this one would flush the view
    /// before freezePriceOrder clears `_sortWaiting`, leaving the "Pricing N packages" notice on
    /// screen over an already-sorted list until some later cycle happened to repaint it.
    private applySortOrder(): void {
        const run = this.activeRun;
        if (!run) {
            return;
        }
        // Risk band, then price, then fewer traded legs, then fewer cards (owner 2026-08-11
        // evening, extended CR-20260824-003). Cards are empty on the LLM and Close paths — those
        // tie at zero and fall through to the engine's order, which is exactly the pre-ruling
        // behaviour. A risk key left on while nothing carries a band separates nothing, so the
        // single-level list is untouched by it.
        const ordered = sortedByKeys(this.engineOrderedSolutions, this._sortKeys,
            s => this.solutionRiskBand(s),
            s => this.solutionLedgerPrice(s),
            s => s.content && s.content.legs ? s.content.legs.length : 0,
            s => s.content && s.content.cards ? s.content.cards.length : 0);
        this._sortedIds[run.id] = ordered.map(s => s.id);
        this.pruneSortedOrders();
    }

    /// Keep only the orders belonging to a run some tab is still showing. Without this the map
    /// would hold one array per run ever made in the session — small, but unbounded is unbounded.
    private pruneSortedOrders(): void {
        const live: { [runId: string]: true } = {};
        const kinds: TabKind[] = ['select', 'adjust', 'close'];
        for (let i = 0; i < kinds.length; i++) {
            const t = this._session.tab(kinds[i]);
            if (t && t.currentRun) {
                live[t.currentRun.id] = true;
            }
        }
        const held = Object.keys(this._sortedIds);
        for (let i = 0; i < held.length; i++) {
            if (!live[held[i]]) {
                delete this._sortedIds[held[i]];
            }
        }
    }

    /// The settle: order the list AND stop waiting. Everything that ends the wait is here, so a
    /// manual re-order (applySortOrder alone) can never silently cancel a pending settle.
    ///
    /// ⚠ ORDERS ONLY ITS OWN RUN. The 10-second deadline can fire while the trader is looking at a
    /// DIFFERENT tab, and applySortOrder always orders whatever tab is in front of them — writing
    /// that tab's order here would be the settle silently re-ranking a list it was not waiting on.
    @DetectMethodChanges()
    private freezePriceOrder(): void {
        const run = this.activeRun;
        if (run && run.id === this._sortRunId) {
            this.applySortOrder();
        }
        this._sortWaiting = false;
        this.clearSortTimer();
    }

    /// A package's own risk band, read off its title — the wire carries no risk field. See
    /// riskBandOf for why that is a parse rather than a lookup.
    private solutionRiskBand(sol: Solution): RiskBand | undefined {
        return riskBandOf(sol && sol.content ? sol.content.displayName : undefined);
    }

    // ---- R9 (CR-20260805-001): hide packages that cost more than the minimum profit ----------
    //
    // Adjust tab, the two forms that name a minimum over a bounded stretch - RANGE and, since
    // CR-20260806-001, FILL GAP. Judged against the RUN's sent inputs, never the live form state,
    // which the trader may have edited since. undefined = the filter is OFF (wrong tab, wrong form,
    // no succeeded run, or a raw minimum that cannot be normalized) and nothing is hidden - failing
    // OPEN, because hiding answers on a guessed basis is the losing side of that bet.
    //
    // ⚠ WHY FILL GAP WAS EXCLUDED, AND WHY THAT REASON IS GONE. The exclusion was CR HYGIENE, not
    // domain: AC-9 required that CR to be inert on every form but Range. The rule itself never had
    // anything to do with the beyond - paying 16 to guarantee 15 is a losing trade whichever form
    // named the extent (owner, 2026-08-06: *"whatever rules make sense within the Range mode also
    // make sense for the Fill Gap mode"*). Wall and Follow-Through stay out: their minimum is not
    // a promise over a bounded stretch in the same sense, and no ruling has been taken on them.
    get minProfitFilterPoints(): number | undefined {
        if (this.activeTab !== 'adjust') {
            return undefined;
        }
        const run = this.activeRun;
        if (!run || run.outcome !== 'succeeded' || run.tabKind !== 'adjust') {
            return undefined;
        }
        const inputs = run.sentInputs as AdjustInputs;
        const kind = inputs && inputs.priceRange ? inputs.priceRange.kind : undefined;
        if (kind !== 'bounded' && kind !== 'fillGap') {
            return undefined;   // Range and Fill Gap only - Wall and Follow-Through are unruled
        }
        const defQty = this.ctx ? this.ctx.readDefaultQty() : undefined;
        return minProfitAsPoints(inputs.minProfit, inputs.minProfitNormalized, defQty);
    }

    // One package's ledger price (credit > 0 / debit < 0), from the SAME shared math the card
    // header shows - computeComboNet + ledgerPrice, live mids, ES normalization, all-or-nothing.
    // A second implementation here would be free to disagree with the number the trader reads.
    private solutionLedgerPrice(sol: Solution): number | undefined {
        const legs = sol && sol.content ? sol.content.legs : [];
        const combo = computeComboNet(
            legs,
            ticker => {
                const q = this._lastQuoteCache.getLastQuote(ticker);
                if (isVoid(q)) {
                    return undefined;
                }
                return (typeof q.mid === 'number' && !isNaN(q.mid)) ? q.mid : undefined;
            },
            asset => this.ctx ? this.ctx.getNormFactor(asset) : 1
        );
        return ledgerPrice(combo, this.ctx ? this.ctx.readDefaultQty() : undefined);
    }

    // ---- THE RESULT FILTERS (owner, 2026-08-06) --------------------------------------------
    //
    // Three OPTIONAL checkboxes. The engine keeps producing everything; only the panel hides.
    // *"I want the engine to produce them, but I want to be able to hide them from the results
    // optionally."*
    //
    // ⚠ FILTERS, NOT A RE-RANK. The one-shot price sort is untouched (ruling F4, price is king) -
    // hiding a card does not move any other card.
    //
    // DEFAULTS — ALL THREE ON (owner, 2026-08-07). This is deliberately NOT the pre-CR behaviour,
    // where only the cost filter ran: the owner's position is that a package he would not trade is
    // noise whether it is too expensive, steps on a strike, or does not fit what he asked for.
    // ⚠ Consequence, so it is not read as a bug: the panel now hides a LOT out of the box - on the
    // reported book, 38 of 42 - and reaching the all-hidden box on the first run is normal rather
    // than exceptional. That is why the box below names the filters as the cause.
    //
    // ⚠ NOT PERSISTED, and that is a decision rather than an omission: nothing under copilot/
    // persists per-user UI state today, and a filter is a reading preference, not portfolio data.
    // It DOES survive a new run, a New Conversation and a portfolio switch, because those toggle
    // `contentVisible` - which recreates the TEMPLATE, not this component. Do not "fix" that by
    // resetting on run: a filter the trader turned on is a standing choice, and silently re-showing
    // what he hid is its own kind of surprise.
    // ⚠ ONE result filter, deliberately (CR-20260810-001): step-on-strikes packages are refused
    // inside the engine (R-F, PackageGates gate 3) and the strict-delivery filter is reverted
    // (R-A: the request is a floor). Do not rebuild a panel filter over an engine rule — it
    // hides the day the two ever disagree.
    //
    // ⚠⚠ NO LONGER REACHABLE FROM THE GUI, AND OFF BY DEFAULT (owner screencast, 2026-08-24:
    // "we don't need this hide packages that cost more than they bring - remove that checkbox").
    // The CHECKBOX is gone; this code is kept ON PURPOSE at the owner's instruction ("don't delete
    // it from the code yet"), so read the state below as DORMANT, not as live behaviour:
    //   - `tooExpensive` starts FALSE, so isHiddenSolution() answers false for every package and
    //     everything the engine returned is on screen;
    //   - nothing calls onResultFilterChanged() any more, so it can never turn true at runtime;
    //   - therefore hiddenCounts is all-zero, allSolutionsHidden is false, and the
    //     "No packages match your filter" box below is unreachable until a caller is put back.
    // Flipping this one default back to `true` and restoring the checkbox is the whole revert.
    resultFilters = {
        tooExpensive: false
    };

    @DetectMethodChanges()
    onResultFilterChanged(key: 'tooExpensive', value: boolean): void {
        this.resultFilters[key] = value === true;
    }

    // The card's *ngFor hides, never destroys, a filtered package (CSS class): destroying it would
    // drop its ref-counted quote subscriptions, freeze its price at the last seen mid, and latch it
    // hidden forever - the cost filter must stay a live snapshot, not a latch.
    isHiddenTooExpensive(sol: Solution): boolean {
        return this.resultFilters.tooExpensive
            && isTooExpensive(this.solutionLedgerPrice(sol), this.minProfitFilterPoints);
    }

    /// The template's single question. Kept as one method so the card markup carries one binding.
    isHiddenSolution(sol: Solution): boolean {
        return this.isHiddenTooExpensive(sol);
    }

    // ---- NEVER A SILENT CULL -----------------------------------------------------------------
    //
    // A filter counts EVERY package tripping ITS OWN criterion, standalone (owner, 2026-08-07:
    // *"I want, next to every label, the count of how many packages fall into that criterion"*).
    // Should a second filter ever exist again, the counts stay per-criterion and deliberately
    // need not add up - a fixed precedence was tried and rejected on the owner's own book.
    //
    // ⚠ `total` IS NOT THE SUM AND MUST NEVER BECOME IT. It counts DISTINCT hidden packages,
    // because `allSolutionsHidden` compares it against `activeSolutions.length` to decide whether
    // to show the everything-is-filtered box.
    get hiddenCounts(): { tooExpensive: number; total: number } {
        const counts = { tooExpensive: 0, total: 0 };
        const sols = this.activeSolutions;
        for (let i = 0; i < sols.length; i++) {
            const sol = sols[i];
            let hidden = false;
            if (this.isHiddenTooExpensive(sol)) {
                counts.tooExpensive++;
                hidden = true;
            }
            if (hidden) {
                counts.total++;
            }
        }
        return counts;
    }

    // THE RUN NOTES NO LONGER REACH THE VIEW (owner, 2026-08-24). `runNotes` stood here and fed the
    // amber notes box in the template; both are gone - the owner does not want the engine's
    // warnings and explanations on the panel.
    // The DATA is untouched on purpose: the notes still arrive on CoPilotSolutionsReadyDto.notes,
    // completeRun() still stores them on the run, and the engine still writes them backend-side to
    // shell.trace.txt - which is now the only place to read them. Do not strip the wire or the
    // session field to "clean up": that would delete the backend's record, which was kept
    // deliberately.

    // True when the filters have hidden EVERY answer. The panel must say so in words: an empty
    // list here means "you hid them", which is a different fact from "the engine found nothing"
    // (that run has outcome 'empty' and renders the No-suggestions box instead).
    get allSolutionsHidden(): boolean {
        const total = this.activeSolutions.length;
        return total > 0 && this.hiddenCounts.total === total;
    }

    // The all-hidden box's second line. It states the COUNT the engine returned, because the one
    // thing the trader must not conclude from an empty results area is that there was nothing to
    // show - and it names the filters as the cause so the fix is one click away, not a re-run.
    get allHiddenMessage(): string {
        const total = this.activeSolutions.length;
        return total === 1
            ? 'The One Package ORION Returned is Hidden by the Filters Above. Uncheck a Filter to See it.'
            : 'All ' + total + ' Packages ORION Returned Are Hidden by the Filters Above. '
                + 'Uncheck a Filter to See Them.';
    }

    // CR-20260924-002: every book read below follows the ES toggle (inEsToggleScope) — the dates
    // are narrowed to the hedges / positions the toggle shows, never read wider than them.
    get bookDates(): Date[] {
        const keys: { [k: string]: boolean } = {};
        this.bookHedges.forEach(h => (h.expirations || []).forEach(e => { keys[e] = true; }));
        return (this.ctx ? this.ctx.readBookDates() : []).filter(d => keys[toCalendarDateString(d)] === true);
    }

    get bookHedges(): CoPilotBookHedge[] {
        return (this.ctx ? this.ctx.readBookHedges() : []).filter(h => this.inEsToggleScope(h.underlying));
    }

    // Expirations offered on the ADJUST tab (CR-20260814-001). SEPARATE from bookDates on purpose —
    // see IHedgingGridCopilotFacade.readAdjustDates. Do not swap one for the other.
    get adjustDates(): Date[] {
        const keys: { [k: string]: boolean } = {};
        this.readBookPositions().forEach(p => { if (p.expiry) { keys[p.expiry] = true; } });
        return (this.ctx ? this.ctx.readAdjustDates() : []).filter(d => keys[toCalendarDateString(d)] === true);
    }

    // ---- stale-snapshot indicator ----

    // The classes for the refresh button: ['copilot-stale'] when the live matrix diverges from the last
    // sync, else []. Same control method as the portfolio-selector refresh button (etsNgClassAsync pipe).
    getCopilotRefreshCss(): Promise<string[]> {
        // CR-20260831-004 owner ruling (screencast, defect round 1): the refresh button reports
        // UNDERLYING/BOOK drift only — matrix drift + the embedded copilot's GRID drift. Changed
        // parameters have their own per-control yellow and light the FAB, never this button.
        return Promise.resolve((this.isMatrixStale() || this._embeddedGridStale) ? ['copilot-stale'] : []);
    }

    // CR-20260831-004: any Adjust/Close parameter changed since that tab's current run. The Select
    // tab's own param diff arrives merged into _embeddedStale.
    get hasParamChanges(): boolean {
        return hasAnyChange(this.adjustParamChanges) || hasAnyChange(this.closeParamChanges);
    }

    // True iff the Conversation holds at least one Solution on ANY tab — "CoPilot has results calculated".
    // Cross-tab like hasActivePreview, and for the same reason: the cue is about the Conversation, not the
    // tab the user happens to be looking at.
    get hasResults(): boolean {
        const conv = this._session.conversation;
        if (!conv) {
            return false;
        }
        const tabs = [conv.tabs.select, conv.tabs.adjust, conv.tabs.close];
        return tabs.some(t => t.solutions.length > 0);
    }

    // True when the live matrix fingerprint differs from the captured baseline. Pure (no side effects):
    // baseline upkeep happens at the sync points + the heartbeat, never here.
    //
    // RESULTS GATE (owner 2026-08-03): drift only matters once there is something for it to invalidate.
    // With no Solutions on the board the trader is still setting up — adding and acquiring hedges before
    // ever running CoPilot is the normal opening move, and flagging it as drift cried wolf. The Run itself
    // re-baselines to the book it fired against (onRun), so gating here loses no real warning: any edit
    // made BEFORE the run is already inside the payload, and any edit made AFTER it still trips the cue.
    isMatrixStale(): boolean {
        if (this._matrixSyncSignature === null || this._suppressStaleCheck || this._additionalWriting) {
            return false;
        }
        if (!this.hasResults) {
            return false;
        }
        return this.computeMatrixSignature() !== this._matrixSyncSignature;
    }

    // A deterministic fingerprint of EXACTLY the matrix reads CoPilot consumes (existing book hedges +
    // their held legs), sorted so leg/hedge ordering never matters. ES uses actualStrike (the real strike,
    // not the fair-value-adjusted one) so a fair-value re-fetch does not move the fingerprint. New (isNew)
    // hedges are already filtered out by the facade reads, so an uncommitted hedge never trips staleness.
    private computeMatrixSignature(): string {
        if (!this.ctx) {
            return this._emptyMatrixSignature;
        }
        // CR-20260924-002: the reads follow the ES toggle, and the mode itself is a member — a flip
        // changes the book ORION would consume, so it reads as drift until the next run / refresh.
        const positions = this.readBookPositions()
            .map(p => `${p.groupId}|${p.ticker}|${p.quantity}|${p.strike}|${p.expiry}|${p.optionType}`)
            .sort();
        const hedges = this.bookHedges
            .map(h => `${h.id}|${h.optionType}|${(h.expirations || []).slice().sort().join(',')}`)
            .sort();
        return JSON.stringify({ p: positions, h: hedges, m: this.esMode });
    }

    // Re-baseline: "CoPilot is now in sync with the matrix". Called at every CoPilot<->matrix sync point
    // (conversation start, run, Preview/un-Preview) so CoPilot's OWN writes never read as staleness.
    private captureMatrixSync(): void {
        this._matrixSyncSignature = this.computeMatrixSignature();
    }

    // Startup-race guard: the first baseline can be captured while the grid is still loading positions
    // (empty). Adopt the first POPULATED read as the real baseline instead of flagging the async load as a
    // user change. Runs off the heartbeat, before the button re-renders, so there is no yellow flash.
    private adoptEmptyBaselineIfNeeded(): void {
        if (this._matrixSyncSignature !== this._emptyMatrixSignature) {
            return;
        }
        const live = this.computeMatrixSignature();
        if (live !== this._emptyMatrixSignature) {
            this._matrixSyncSignature = live;
        }
    }

    // Hg.HedgeMatrixChanged handler: the decorator forces a CD pass so the OnPush refresh button re-checks
    // staleness AND the child solution cards (checked in the same detectChanges) re-check their Modified
    // badge; the body just tends the startup baseline. The looks are recomputed by getCopilotRefreshCss /
    // the card's isModified getter.
    @DetectMethodChanges()
    onMatrixChanged(): void {
        this.adoptEmptyBaselineIfNeeded();
        this._emitStale();   // a user edit drifted the matrix (or a revert restored it) => push the launcher cue
        // A matrix change can move the embedded copilot's current date (the nearest hedge
        // expiration) — relay so its own stale cue and live override feeds re-check too.
        if (this._selectCopilot) {
            this._selectCopilot.onGridStateMaybeChanged();
        }
    }

    // The underlying instrument + the nearest-available expiration that drive the questionnaire's strike
    // dropdowns. Held as FIELDS (not getters), refreshed on the Hedge Matrix's Hg.HedgeMatrixStrikeContextChanged
    // signal (+ on init) so the OnPush bindings to the tabs reliably update once the matrix resolves the
    // instrument async. Strike selection is date-INDEPENDENT (owner rule 2026-06-21) — always the nearest
    // available expiration, so one value serves both tabs.
    targetInstrument: TradingInstrument | null = null;
    strikeExpiration: OptionExpirationDescriptor | null = null;
    private _strikeExpSeq = 0;

    // The underlying's listed expiration dates ("yyyy-MM-dd") — restricts the Select tab's date-boxes
    // to tradeable dates. expirationsLoading is true until the (async) chain fills this in, so the
    // date-boxes fall back to weekends-only + show a loading indicator meanwhile.
    availableExpirations: string[] = [];
    expirationsLoading = false;

    // ---- The Select tab's OPG-parity context (CR-20260804-001) ----------------------------

    // The panel's CURRENT DATE (the owner's 2026-08-04 rule): hedges in the book -> the
    // NEAREST hedge expiration; empty book -> the nearest chain expiration. "Nearest"
    // includes today (0d). Feeds the Select tab's first date pill + the override date label.
    get selectCurrentDateKey(): string | null {
        const today = toCalendarDateString(new Date());
        let best: string | null = null;

        const hedges = this.bookHedges;
        for (let i = 0; i < hedges.length; i++) {
            const dates = hedges[i].expirations || [];
            for (let d = 0; d < dates.length; d++) {
                if (dates[d] >= today && (best == null || dates[d] < best)) {
                    best = dates[d];
                }
            }
        }

        if (best != null) {
            return best;
        }

        const listed = this.availableExpirations;
        for (let i = 0; i < listed.length; i++) {
            if (listed[i] >= today && (best == null || listed[i] < best)) {
                best = listed[i];
            }
        }

        return best;
    }

    // (The date-pill TEXT is the embedded copilot's own formatter now — the '(Nd)' display
    // delta of the parity build dissolved with it.)

    private refreshStrikeContext(): void {
        this.targetInstrument = this.ctx ? this.ctx.readTargetInstrument() : null;
        this.refreshAvailableExpirations();
        // Drive the embedded copilot's horizontal-skew instance the way the OPG panel drives
        // its own: init once with the underlying, then follow underlying changes. Prefs are
        // namespaced to this host so the two panels' HS settings never collide.
        const ul = this.underlyingTicker();
        if (ul) {
            if (!this._hsInited) {
                this._hsInited = true;
                Promise.resolve(this._horizontalSkew.init(ul, 'hgCopilot')).catch(() => { /* HS box shows no reading */ });
            } else {
                this._horizontalSkew.setUnderlying(ul);
            }
        }
        // The strike context is part of the embedded copilot's grid fingerprint (symbol,
        // current date) — let it re-check its stale cue + live feeds.
        if (this._selectCopilot) {
            this._selectCopilot.onGridStateMaybeChanged();
        }
        this._changeDetector.markForCheck();
        this.resolveNearestExpiration();
    }

    // Read the tradeable-date universe from the cached chain (sync). Empty until the chain loads; this
    // method re-runs on every Hg.HedgeMatrixStrikeContextChanged (the matrix fires it once the chain
    // resolves), so the list fills + expirationsLoading clears on that later refresh.
    private refreshAvailableExpirations(): void {
        this.availableExpirations = this.ctx ? this.ctx.readAvailableExpirations() : [];
        this.expirationsLoading = this.availableExpirations.length === 0;
    }

    private async resolveNearestExpiration(): Promise<void> {
        const seq = ++this._strikeExpSeq;
        const exp = this.ctx ? await this.ctx.resolveNearestStrikeExpiration() : null;
        if (seq !== this._strikeExpSeq) {
            return;   // a newer refresh started (instrument changed again) — drop this stale result
        }
        this.strikeExpiration = exp;
        this._changeDetector.markForCheck();
    }

    // RUN enabled: active tab valid + nothing in flight globally + a portfolio is loaded.
    // Never on the Select tab — the embedded Select Copilot owns its own Run button.
    get canRun(): boolean {
        if (this.activeTab === 'select') {
            return false;
        }
        const st = this._tabState[this.activeTab];
        return !!st && st.valid && !this._session.isRunInFlight && !isVoid(this._portfolioId);
    }

    // A Run is pending on a DIFFERENT tab → this tab is globally unavailable (FR-39).
    get runInProgressElsewhere(): boolean {
        if (!this._session.isRunInFlight) {
            return false;
        }
        const run = this.activeRun;
        return !(run && run.id === this._session.inFlightRunId && run.outcome === 'pending');
    }

    get activeIsPending(): boolean {
        const run = this.activeRun;
        return !!run && run.outcome === 'pending';
    }

    // ---- RUN lifecycle (M2-6) ----

    @DetectMethodChanges()
    onRun(): void {
        if (!this.canRun || this.activeTab === 'select') {
            return;   // Select runs belong to the embedded Select Copilot (its own Run button)
        }
        const kind = this.activeTab;
        const st = this._tabState[kind];
        const inputs = st.inputs;

        // re-run cleanup (FR-104..106): un-Preview the tab's prior CoPilot legs, clear prior
        // solutions/applied/blocked (inputs are kept by the live questionnaire components).
        this.teardownTabPreviews(kind);
        this._session.resetTabForReRun(kind);

        // CR-20260831-004: sentInputs is the param-diff BASELINE now, so it gets its own deep copy
        // — the live questionnaire state must never alias the "snapshot of what was sent". `inputs`
        // (the live reference) still feeds the command build below, unchanged.
        const sent = kind === 'close'
            ? cloneCloseInputs(inputs as CloseInputs)
            : cloneAdjustInputs(inputs as AdjustInputs);
        const run = this._session.startRun(kind, sent);
        if (!run) {
            return;   // lost the single-flight race
        }

        // Collapse this tab's questionnaire to its input summary (the pending spinner + solutions show
        // below). A non-success outcome re-expands it (settleCollapse); a click re-expands it too.
        this.collapsed[kind] = true;
        this._lastRunEngineTab = kind;   // round 3: the tab cue clears (active === last-run)
        this._recomputeParamChanges();   // live == sent at this instant => this tab's param cues clear

        const positions = this.readBookPositions();   // CR-20260924-002: the toggle's book
        // CR-20260918-001: the book the run was fired against, kept for the Transactions popup's
        // BEFORE column. Read once here, never from the live grid later — a Previewed package already
        // sits on the grid, so a later read would show the after-state as the before-state.
        this._heldAtRun[kind] = positions.slice();

        // CR-20260910-001 milestone 2: THE CLOSE RUNS ON THE BACKEND NOW. It used to be synthesized
        // here in the browser and completed inline — deterministic, so there was nothing to ask a
        // skill. It still is deterministic; what changed is WHERE, and why: the same synthesis and
        // the same pricing now serve Maven in process, and a second copy of them in the browser
        // would be free to drift from the one the packages are built on.
        //
        // Its OWN command, and no cancel: a close ends by completing, by its deadline, or by being
        // superseded when Apply is pressed again.
        if (kind === 'close') {
            const closeCmd = this.buildCloseRunCommand(run.id, inputs as CloseInputs, positions);
            this._shellClient.processCommand(closeCmd).catch(() => this.onAckFailure(run.id));
            this.startBackstop(run.id);
            this.captureMatrixSync();   // re-baseline to the matrix the run was fired against
            this._emitStale();          // re-baseline cleared any prior drift => clear the launcher cue
            return;
        }

        const cmd = this.buildRunCommand(run.id, kind, inputs, positions);
        // async-fire: the ack returns immediately; the result arrives over ESS (correlated by runId).
        this._shellClient.processCommand(cmd).catch(() => this.onAckFailure(run.id));
        this.startBackstop(run.id);
        this.captureMatrixSync();   // re-baseline to the matrix the run was fired against
        this._emitStale();          // re-baseline cleared any prior drift => clear the launcher cue
    }

    @DetectMethodChanges()
    onCancel(): void {
        const run = this.activeRun;
        if (!run || run.outcome !== 'pending') {
            return;
        }
        // CR-20260910-001 milestone 2. A CLOSE CANNOT BE CANCELLED and the button is not rendered
        // for it; this is the second lock on the same door, so a stray call cannot reach a command
        // the Close backend does not implement. Sending CancelCoPilotRun here would address the
        // ADJUST engine's registry, which has never heard of this run.
        if (run.tabKind === 'close') {
            return;
        }
        this._session.cancelRun(run.id);            // releases the lock immediately (no 60s wait)
        this.collapsed[run.tabKind] = false;        // re-expand so the user can edit + re-run
        this.clearBackstop();
        this.teardownTabPreviews(run.tabKind);      // grid untouched (FR-45)
        this._shellClient.processCommand(new CancelCoPilotRun(run.id)).catch(() => { /* best-effort */ });
    }

    // ESS push: correlate by runId; discard late/foreign/superseded results (FR-47).
    private onSolutionsReady(dto: CoPilotSolutionsReadyDto): void {
        if (isVoid(dto) || !this._session.isCurrent(dto.runId)) {
            return;
        }
        this.clearBackstop();
        const outcome = dto.outcome as BackendOutcome;
        const contents = (dto.solutions || []).map(toSolutionContent);
        stampEsGaps(contents, dto.esContracts || null);   // CR-20260924-002
        // FR-55 (prior Previews un-Previewed on a later Run's terminal failure) is already satisfied:
        // onRun() un-Previews the tab's prior solutions before starting, so none remain by failure time.
        const ok = this._session.completeRun(dto.runId, outcome, contents, dto.failureDetail || null,
            dto.notes || null);
        if (ok) {
            this.settleCollapse(this.findRunTab(dto.runId), outcome as RunOutcome, dto.failureDetail || null);
            if (outcome === 'succeeded' && contents.length > 0) {
                // Results are here but UNPRICED — the engine never prices. Hold the engine's order
                // and start waiting for the quotes that decide the real one.
                this.armPriceSort(dto.runId);
            }
        }
        // Arriving results can flip the stale gate on by themselves: an edit made while the Run was in
        // flight already drifted the matrix, but hasResults was false so nothing was emitted. Re-check
        // here — the baseline is NOT touched (the run's book stands), only the emission is re-evaluated.
        this._emitStale();
        this._changeDetector.detectChanges();
    }

    // The CLOSE's push (CR-20260910-001 milestone 2). Same settle path as Adjust's — correlate by
    // runId, discard anything late or foreign, complete the run, arm the price sort — because the
    // cards, Preview, Apply and the transactions view downstream are the same machinery. Only the
    // wire shape differs, and that is what toCloseSolutionContent absorbs.
    //
    // ⚠ THE DELIVERED PRICES ARE NOT RENDERED, and that is the contract, not an oversight. The
    // close cards are quote-live: they price from the last-quote cache and keep re-pricing, and the
    // delivery deliberately carries no time stamp because there is no snapshot moment to show.
    // Seeding a card with a figure computed a second ago would put exactly that stale moment back
    // on screen. The backend's figures serve the callers that cannot watch quotes — Maven, in
    // process — and the notes below, which name any hedge the market could not price.
    private onCloseSolutionsReady(dto: HmCloseSolutionsReadyDto): void {
        if (isVoid(dto) || !this._session.isCurrent(dto.runId)) {
            return;
        }
        this.clearBackstop();
        const outcome = dto.outcome as BackendOutcome;
        const contents = (dto.solutions || []).map(toCloseSolutionContent);
        // The wire carries a refusal NAME from a closed list; the panel toasts whatever is here
        // verbatim, so it is turned into a sentence first. See closeRefusalText.
        const detail = closeRefusalText(dto.failureDetail || null);
        const ok = this._session.completeRun(dto.runId, outcome, contents, detail, dto.notes || null);
        if (ok) {
            this.settleCollapse(this.findRunTab(dto.runId), outcome as RunOutcome, detail);
            if (outcome === 'succeeded' && contents.length > 0) {
                // Armed for the same reason Adjust's list is: the solutions arrive UNPRICED as far
                // as this panel is concerned, so the engine's order stands only until the quotes
                // that decide the real one have arrived.
                this.armPriceSort(dto.runId);
            }
        }
        this._emitStale();
        this._changeDetector.detectChanges();
    }

    private onAckFailure(runId: string): void {
        if (!this._session.isCurrent(runId)) {
            return;
        }
        this.clearBackstop();
        const msg = 'Could Not Reach the ORION Service.';
        const ok = this._session.completeRun(runId, 'terminalFailed', [], msg);
        if (ok) {
            this.settleCollapse(this.findRunTab(runId), 'terminalFailed', msg);
        }
        this._changeDetector.detectChanges();
    }

    // The positions an ADJUST run is allowed to see: only those expiring on the chosen expiration
    // (CR-20260814-001).
    //
    // THIS IS THE WHOLE MECHANISM OF THE SELECTOR. The engine derives the run's expiration from the
    // positions it is handed (AdjustRunMapper.ResolveInstrument) and builds its payoff terms from the
    // same list, so narrowing the list here is what makes the chosen expiration's line — and nothing
    // else — the line being shaped. Hedges in other expirations are not hidden from the answer; they
    // are absent from the question.
    //
    // Nothing is filtered when no expiration is set: that state cannot reach a Run (isAdjustComplete
    // refuses it), and silently sending an empty array would turn a UI bug into a mute backend refusal.
    // Left whole, a bug here instead trips AdjustRunMapper's "more than one expiration" by name.
    private positionsForAdjust(positions: any[], inputs: AdjustInputs): any[] {
        if (!inputs || !inputs.expiration) {
            return positions;
        }
        const key = toCalendarDateString(inputs.expiration);
        return (positions || []).filter(p => p.expiry === key);
    }

    private buildRunCommand(runId: string, kind: TabKind, inputs: AdjustInputs | CloseInputs, positions: any[]): RunCoPilotSkill {
        const defaultQty = this.ctx ? this.ctx.readDefaultQty() : undefined;
        // Select never reaches this wire since the two-copilots reversal — the Select tab fires
        // the Select Copilot's own operation (RunOpgCoPilotSkill) from inside the embed.
        const select = null;
        const adjust = kind === 'adjust' ? mapAdjustInputs(inputs as AdjustInputs, defaultQty) : null;
        // CR-20260910-001 milestone 2: the close slot travels NULL and always will. The Close is
        // its own command now (buildCloseRunCommand), and a close never reaches this method — the
        // run path returns before it. The slot itself stays on the wire type because the backend's
        // dormant model is still nested in this live command and was deliberately left untouched.
        const close = null;
        // Scoped HERE rather than at the caller's readWorkingStatePositions so the Close path cannot be
        // touched by it — Close returns before this method is ever reached and keeps the whole book.
        const sentPositions = kind === 'adjust'
            ? this.positionsForAdjust(positions, inputs as AdjustInputs)
            : positions;
        // CR-20260924-002: ES mode rides the Adjust command — the positions above are the ES
        // hedges (the toggle's book) and the backend lens takes them to SPX terms and back.
        const esMode = this.esMode;
        return new RunCoPilotSkill(
            COPILOT_CONTRACT_VERSION,
            runId,
            this._portfolioId || '',
            kind,
            sentPositions,
            select,
            adjust,
            close,
            esMode,
            esMode && this.ctx ? this.ctx.readBaseAsset() : null,
            esMode && this.ctx ? this.ctx.readEsContractCandidates() : null
        );
    }

    // The CLOSE's own command (CR-20260910-001 milestone 2, contract H1 section 3).
    //
    // It sends the WHOLE working-state book, not a narrowed slice: the chosen dates and hedges are
    // in the questionnaire, and the backend filters against them. That is also what lets the
    // backend refuse by name when a date or a hedge is no longer on the book. Since CR-20260924-002
    // "the whole book" is the whole book THE ES TOGGLE SHOWS (ON = ES hedges, OFF = SPX hedges) —
    // the same set the Close tab lists, so a named refusal still names something on screen.
    //
    // defaultQty is the LEDGER PRICE's denominator, and the panel's own value — the loaded
    // portfolio's default order quantity. Absent, the backend leaves the ledger equal to the order
    // price rather than inventing a scale.
    private buildCloseRunCommand(runId: string, inputs: CloseInputs, positions: any[]): RunHedgeMatrixCloseSkill {
        const defaultQty = this.ctx ? this.ctx.readDefaultQty() : undefined;
        return new RunHedgeMatrixCloseSkill(
            HM_CLOSE_CONTRACT_VERSION,
            runId,
            this._session.panelInstanceId,
            this._portfolioId || '',
            positions,
            mapCloseInputs(inputs),
            isValidNumber(defaultQty, true) ? defaultQty : 0,
            this.ctx ? this.ctx.readBaseAsset() : null
        );
    }

    private startBackstop(runId: string): void {
        this.clearBackstop();
        this._backstopHandle = setTimeout(() => this.onBackstop(runId), FE_BACKSTOP_MS);
    }

    private clearBackstop(): void {
        if (this._backstopHandle) {
            clearTimeout(this._backstopHandle);
            this._backstopHandle = null;
        }
    }

    private onBackstop(runId: string): void {
        if (!this._session.isCurrent(runId)) {
            return;
        }
        const msg = 'No Response Within the Time Limit.';
        const ok = this._session.completeRun(runId, 'terminalFailed', [], msg);
        if (ok) {
            this.settleCollapse(this.findRunTab(runId), 'terminalFailed', msg);
        }
        this._changeDetector.detectChanges();
    }

    // Map a settled run back to its tab (the run stays attached to tab.currentRun after completion).
    private findRunTab(runId: string): TabKind | null {
        const conv = this._session.conversation;
        if (!conv) {
            return null;
        }
        const t = conv.tabs;
        if (t.select.currentRun && t.select.currentRun.id === runId) { return 'select'; }
        if (t.adjust.currentRun && t.adjust.currentRun.id === runId) { return 'adjust'; }
        if (t.close.currentRun && t.close.currentRun.id === runId) { return 'close'; }
        return null;
    }

    // After a run settles: a NON-SUCCESS outcome re-expands the form so the user can fix + re-run, and a
    // failure also raises an error toast (owner 2026-06-22). 'succeeded'/'empty' stay collapsed — the
    // solutions / "no results" panel show below the summary.
    private settleCollapse(kind: TabKind | null, outcome: RunOutcome, errorMessage: string | null): void {
        if (!kind) {
            return;
        }
        if (outcome === 'terminalFailed' || outcome === 'preSendBlocked') {
            this.collapsed[kind] = false;
            this._toastr.error(errorMessage || 'ORION Could Not Produce a Result. Adjust Your Inputs And Run Again.', 'ORION');
        } else if (outcome === 'cancelled') {
            this.collapsed[kind] = false;
        }
    }

    // ---- card actions (M2-7) ----

    // Preview / un-Preview (grid effect via the facade — M3). Apply/Block do NOT touch the grid.
    @DetectMethodChanges({ isAsync: true })
    async onPreviewToggle(sol: Solution): Promise<void> {
        // Workflow guard (owner): cannot un-Preview a Solution that is still applied — un-Apply first. The
        // card disables the Preview button in this state; this is the defensive backstop.
        if (sol.state.previewed && sol.state.applied) {
            return;
        }
        if (!this.ctx) {
            this._toastr.info('Preview Becomes Available Once ORION is Connected to the Grid.');
            return;
        }
        // Preview / un-Preview is CoPilot's OWN matrix write. Suppress the stale indicator while it settles,
        // then re-baseline in the finally so this self-inflicted change never reads as a user edit (and the
        // finally also covers an early return or a thrown facade call).
        this._suppressStaleCheck = true;
        try {
            if (sol.state.previewed) {
                await this.ctx.removeCopilotLegs(sol.id);
                sol.state.previewed = false;
                this._emitPreviewActive();
                return;
            }
            // Within-tab REPLACE (FR-80) — Select/Adjust only: at most ONE Solution previewed per tab
            // (alternative suggestions). Previewing a 2nd un-Previews the tab's current one first.
            // EXCEPTION (CR-20260619-001 MVP): the CLOSE tab's solutions are INDEPENDENT hedges (one hedge =
            // one Solution), so they are preview-ADDITIVE — previewing one must NOT un-Preview the others
            // (the user closes several hedges at once). Other tabs' Previews always stay (cross-tab union).
            if (this.activeTab !== 'close') {
                const priors = this.activeSolutions.filter(s => s !== sol && s.state.previewed);
                for (const prior of priors) {
                    await this.ctx.removeCopilotLegs(prior.id);
                    prior.state.previewed = false;
                }
            }
            // Cards ride along so the grid can place ONE HEDGE PER CARD (CR-20260724-001) — the legs
            // are the same either way; the cards only say which hedge each belongs in.
            const result = await this.ctx.applySolution(sol.id, sol.content.legs, sol.content.cards);
            if (result.applied) {
                sol.state.previewed = true;
            } else {
                const reason = result.reason;
                if (reason === 'missing-target-leg') {
                    this._toastr.error('Cannot Preview: This Solution Adjusts or Closes a Hedge That is Not in the Loaded Book (Nothing Was Placed).');
                } else if (reason === 'hedge-modified') {
                    this._toastr.info('This Hedge Has Manual Changes, so ORION Will Not Overwrite it. Clear Those Changes (Exit Its Modification Mode) or Start a New Conversation to Close it With ORION.');
                } else if (reason === 'hedge-locked') {
                    this._toastr.error('Cannot Preview: Another Previewed Solution is Already Adjusting or Closing One of These Hedges. Un-Preview That One First.');
                } else {
                    this._toastr.warning('Could Not Preview This Solution: a Leg Could Not be Placed (Nothing Was Placed).');
                }
            }
            // Cover BOTH outcomes: a successful Preview (this Solution now previewed) AND a failed one that
            // still un-previewed the tab's prior Solution above (within-tab REPLACE) — either flips "any previewed".
            this._emitPreviewActive();
        } finally {
            this.captureMatrixSync();
            this._suppressStaleCheck = false;
            this._emitStale();   // self-inflicted Preview write re-baselined => never reads as drift on the launcher
        }
    }

    @DetectMethodChanges()
    onApplyToggle(sol: Solution): void {
        // Workflow guard (owner): Apply follows Preview — you can only Apply a Solution that is being
        // previewed (un-Apply is always allowed). The card disables the Apply button when not previewed.
        if (!sol.state.applied && !sol.state.previewed) {
            return;
        }
        const sols = this.activeSolutions;
        if (sol.state.applied) {
            // Close: independent hedges -> per-solution un-Apply (must not clear the other applied hedges).
            if (this.activeTab === 'close') {
                unApplySolutionAdditive(sols, sol.id);
            } else {
                unApplySolution(sols);            // FR-89/149: un-Apply never touches the grid
            }
            return;
        }
        // Close: ADDITIVE Apply — independent hedges, no sibling-blocking (mirrors the preview-additive
        // exception in onPreviewToggle). FR-90/91 one-of-N blocking does NOT apply to the close tab.
        if (this.activeTab === 'close') {
            applySolutionAdditive(sols, sol.id);
            return;
        }
        // FR-90/91: a Blocked sibling is not choosable while another is Applied — refuse with a plain
        // message rather than silently doing nothing (NFR-22). Apply never mutates the grid (FR-85/125).
        if (sol.state.blocked) {
            this._toastr.info('This Solution is Blocked: Another Solution in This Tab is Already Applied. Un-Apply That One First.');
            return;
        }
        applySolution(sols, sol.id);
    }

    onOpenTransactions(_sol: Solution): void {
        // the read-only Transactions view is owned by the card popup; nothing to do here.
    }

    // ---- Close-All aggregate actions (CR-20260619-001, Close-All increment) ----

    // Preview / un-Preview EVERY close Solution at once — the same net grid effect as previewing each
    // per-hedge card by hand (close previews are ADDITIVE; one never un-Previews another). The aggregate
    // card disables this while any child hedge is Modified/Removed (owner: disable-until-resolved); the
    // guard here is the backstop. Reuses the per-card Preview seam (ctx.applySolution / removeCopilotLegs)
    // so the matrix staging + un-Preview revert are identical to the individual path.
    @DetectMethodChanges({ isAsync: true })
    async onPreviewAllToggle(): Promise<void> {
        if (this.activeTab !== 'close') {
            return;
        }
        // Cannot un-Preview the batch while it is applied (un-Apply first) — the card disables this; backstop.
        if (this.closeAllState.previewed && this.closeAllState.applied) {
            return;
        }
        if (!this.ctx) {
            this._toastr.info('Preview Becomes Available Once ORION is Connected to the Grid.');
            return;
        }
        const sols = this.activeSolutions;
        // Preview/un-Preview is CoPilot's OWN matrix write — suppress the stale indicator while it settles,
        // re-baseline in the finally (covers early return / a thrown facade call), like onPreviewToggle.
        this._suppressStaleCheck = true;
        try {
            if (this.closeAllState.previewed) {
                // Un-Preview ALL: revert every previewed child close.
                for (const s of sols) {
                    if (s.state.previewed) {
                        await this.ctx.removeCopilotLegs(s.id);
                        s.state.previewed = false;
                    }
                }
                this.closeAllState = { previewed: false, applied: false };
                this._emitPreviewActive();
                return;
            }
            // Preview ALL: stage every child not already previewed (absorb any individually-previewed ones).
            const previewedThisCall: Solution[] = [];
            for (const s of sols) {
                if (s.state.previewed) {
                    continue;   // already staged (e.g. previewed individually) — absorb it into the batch
                }
                const result = await this.ctx.applySolution(s.id, s.content.legs, s.content.cards);
                if (result.applied) {
                    s.state.previewed = true;
                    previewedThisCall.push(s);
                } else {
                    // Unexpected (disable-until-resolved guards Modified/Removed). Roll back THIS call's
                    // previews so the batch is all-or-none, then surface a plain message (never silent).
                    for (const done of previewedThisCall) {
                        await this.ctx.removeCopilotLegs(done.id);
                        done.state.previewed = false;
                    }
                    this._toastr.warning('Could Not Preview All Hedges: One Could Not be Staged (Nothing Was Placed).');
                    this._emitPreviewActive();
                    return;
                }
            }
            this.closeAllState = { previewed: true, applied: false };
            this._emitPreviewActive();
        } finally {
            this.captureMatrixSync();
            this._suppressStaleCheck = false;
            this._emitStale();
        }
    }

    // Apply / un-Apply the whole batch (state-only — Apply never touches the grid, FR-91). Only available
    // while the batch is previewed (mirrors the per-card workflow). The child cards stay previewed + locked.
    @DetectMethodChanges()
    onApplyAllToggle(): void {
        if (this.activeTab !== 'close' || !this.closeAllState.previewed) {
            return;
        }
        const sols = this.activeSolutions;
        if (this.closeAllState.applied) {
            sols.forEach(s => { s.state.applied = false; s.state.blocked = false; });   // un-Apply all
            this.closeAllState = { previewed: true, applied: false };
            return;
        }
        sols.forEach(s => { s.state.applied = true; s.state.blocked = false; });          // Apply all (additive)
        this.closeAllState = { previewed: true, applied: true };
    }

    // The aggregate card requested expand/collapse. Owned here so the individual per-hedge cards collapse
    // to the summary line while it is open. Plain field flip; the decorator drives the OnPush CD pass for
    // the whole subtree (the aggregate child reads the new [expanded] in the same detectChanges).
    @DetectMethodChanges()
    onCloseAllExpandedChange(expanded: boolean): void {
        this.closeAllExpanded = expanded;
    }

    // Reset the master state — a fresh close batch on every Conversation boundary / re-run (starts
    // collapsed, with the individual cards shown).
    private resetCloseAllState(): void {
        this.closeAllState = { previewed: false, applied: false };
        this.closeAllExpanded = false;
    }

    // ---- preview-active signal (CR) ----

    // True iff at least one Solution across ALL tabs is currently previewed (its CoPilot legs are placed
    // on the grid). The single source of truth for the host's green "preview active" launcher — covers the
    // Close tab AND Select/Adjust uniformly, since `previewed` is the canonical per-Solution flag.
    get hasActivePreview(): boolean {
        const conv = this._session.conversation;
        if (!conv) {
            return false;
        }
        const tabs = [conv.tabs.select, conv.tabs.adjust, conv.tabs.close];
        return tabs.some(t => t.solutions.some(s => s.state.previewed));
    }

    // Recompute + push the preview-active flag, deduped (emit only on a real change). Called after EVERY
    // mutation of any Solution's previewed state (onPreviewToggle, teardownTabPreviews, teardownAllPreviews).
    private _emitPreviewActive(): void {
        const active = this.hasActivePreview || this._additionalPreviewActive;
        if (active === this._lastEmittedPreviewActive) {
            return;
        }
        this._lastEmittedPreviewActive = active;
        this.previewActiveChange.emit(active);
    }

    // Recompute + push the matrix-stale flag, deduped (emit only on a real transition). Called wherever
    // staleness can flip: a matrix change event (drift appears/clears on a user edit or revert) and every
    // re-baseline/sync point (which clears it). Over-calling is harmless — the dedup guard drops no-ops.
    private _emitStale(): void {
        // The launcher yellow is the union of the staleness sources: the matrix-book drift
        // (Adjust/Close), the embedded Select Copilot's own cue (its grid fingerprint + its own
        // param diff), and — since CR-20260831-004 — Adjust/Close parameters changed since the run.
        const stale = this.isMatrixStale() || this._embeddedStale || this.hasParamChanges
            || this.activeTabNotLastRun;   // round 3: the tab cue joins the union (owner Q1)
        if (stale === this._lastEmittedStale) {
            return;
        }
        this._lastEmittedStale = stale;
        this.staleChange.emit(stale);
    }

    // ---- conversation teardown / re-run (M2-8) ----

    @DetectMethodChanges()
    onNewConversation(): void {
        this._suppressStaleCheck = true;   // suppress until the fresh baseline is captured below
        if (this.ctx) { this.ctx.setOrionEsMode(false); }   // CR-20260924-002: the toggle does not outlive a conversation
        this.teardownAllPreviews();
        if (this.ctx) { this.ctx.resetCopilotDivergence(); }   // FR-103: re-arm the divergence warning on this Conversation boundary
        this.cancelInFlightSilently();
        this.clearBackstop();
        this._tabState = {};
        // CR-20260831-004: refresh = full reset of the run baseline ("as if nothing has been
        // applied") — clear the diffs now; the fresh conversation below has no runs, so they stay
        // empty until the next Run.
        this.adjustParamChanges = {};
        this.closeParamChanges = {};
        this._lastRunEngineTab = null;   // round 3: no runs => no tab cue
        this.collapsed = { select: false, adjust: false, close: false };   // fresh forms = all expanded
        // recreate the questionnaire components (fresh forms) + a fresh Conversation. Keep the user on the
        // tab they are on (owner 2026-06-23): New Conversation clears all state but must NOT jump back to
        // Select. activeTab is a persistent field, so simply not reassigning it preserves the current tab.
        this.contentVisible = false;
        setTimeout(() => {
            this._session.startConversation(this._portfolioId || '');
            this.captureMatrixSync();           // refresh re-syncs the baseline => clears the yellow indicator
            this._suppressStaleCheck = false;
            this._emitStale();                  // baseline reset => clear the launcher cue too
            this.contentVisible = true;
            this._changeDetector.detectChanges();
            // The *ngIf toggle RECREATED the embedded Select Copilot (a fresh conversation by
            // construction — OPG's own reset semantics); open the fresh instance so its state
            // syncs and its width mode re-emits.
            if (this._selectCopilot) {
                this._selectCopilot.onOpened();
            }
        }, 0);
    }

    private onPortfolioChanged(): void {
        this._suppressStaleCheck = true;   // suppress until the fresh baseline is captured below
        if (this.ctx) { this.ctx.setOrionEsMode(false); }   // CR-20260924-002: a new portfolio starts in SPX mode
        this.teardownAllPreviews();
        if (this.ctx) { this.ctx.resetCopilotDivergence(); }   // FR-103: re-arm the divergence warning on this Conversation boundary
        this.cancelInFlightSilently();
        this.clearBackstop();
        this._tabState = {};
        this.adjustParamChanges = {};   // CR-20260831-004: portfolio switch drops the run baseline too
        this.closeParamChanges = {};
        this._lastRunEngineTab = null;
        this.collapsed = { select: false, adjust: false, close: false };   // fresh forms = all expanded
        this.contentVisible = false;
        setTimeout(() => {
            this._session.startConversation(this._portfolioId || '');
            this.activeTab = 'select';
            this.captureMatrixSync();           // new portfolio => new baseline
            this._suppressStaleCheck = false;
            this._emitStale();                  // baseline reset => clear the launcher cue too
            this.contentVisible = true;
            this._changeDetector.detectChanges();
            // Fresh embedded copilot against the (possibly new) underlying — see onNewConversation.
            if (this._selectCopilot) {
                this._selectCopilot.onOpened();
            }
        }, 0);
    }

    private cancelInFlightSilently(): void {
        const id = this._session.inFlightRunId;
        if (!isVoid(id)) {
            this._session.cancelRun(id as string);
            this._shellClient.processCommand(new CancelCoPilotRun(id as string)).catch(() => { /* best-effort */ });
        }
    }

    // un-Preview every Solution that has Preview-state across all tabs (grid effect via M3 facade).
    private teardownAllPreviews(): void {
        this.resetCloseAllState();   // a Conversation boundary clears the close-all master state
        // The embedded Select Copilot's previews live in the adapter's own registry (previews
        // ONLY — never the book); a Conversation boundary clears them the same as the rest.
        this.selectFacade.clearAllStrategies();
        this._embeddedStale = false;
        this._embeddedGridStale = false;
        if (!this.ctx) {
            return;
        }
        const conv = this._session.conversation;
        if (!conv) {
            return;
        }
        const tabs = [conv.tabs.select, conv.tabs.adjust, conv.tabs.close];
        tabs.forEach(t => t.solutions.forEach(s => {
            if (s.state.previewed) {
                this.ctx.removeCopilotLegs(s.id);
                s.state.previewed = false;
            }
        }));
        this._emitPreviewActive();
    }

    private teardownTabPreviews(kind: TabKind): void {
        if (kind === 'close') {
            this.resetCloseAllState();   // a close re-run / cancel starts a fresh batch
        }
        if (!this.ctx) {
            return;
        }
        const t = this._session.tab(kind);
        if (!t) {
            return;
        }
        t.solutions.forEach(s => {
            if (s.state.previewed) {
                this.ctx.removeCopilotLegs(s.id);
                s.state.previewed = false;
            }
        });
        this._emitPreviewActive();
    }
}

// Map the wire Solution DTO -> the immutable domain Value (rendered verbatim, never re-judged).
function toSolutionContent(s: CoPilotSolutionDto): SolutionContent {
    return {
        skillId: s.id,
        skillUniqueId: s.uniqueId,
        displayName: s.displayName,
        shortDescription: s.shortDescription,
        extendedDescription: s.extendedDescription,
        legs: (s.legs || []).map(toLeg),
        // Absent on the LLM path — the card then renders `legs` flat, exactly as before.
        cards: (s.cards || []).map(toPackageCard),
        // ⚠ null COLLAPSES TO undefined DELIBERATELY. The wire sends null for "this form asks no
        // such question" (Wall, Follow-Through) and omits the field entirely on the LLM path; both
        // mean "not measured", and the filter must treat them alike. Writing `?? undefined` rather
        // than `|| undefined` matters: a measured ZERO is the BEST possible answer here, and `||`
        // would throw it away as falsy.
        addedAboveRequestDollars: s.addedAboveRequestDollars ?? undefined,
        outsideMissDollars: s.outsideMissDollars ?? undefined
    };
}

// A card may now target an EXISTING hedge (CR-20260724-001 stage 7).
//
// ⚠ THIS COMMENT USED TO CLAIM `undefined` and `[]` were distinguished here — absent meaning "a new
// hedge", empty meaning "this change closes the hedge". They never were: protobuf-net writes an
// empty repeated field as ABSENT, so `[]` cannot reach this function and every close-the-hedge card
// arrived looking like a card with no outcome. The Matrix refused the whole package for it. The
// engine now STATES it on `closesTheHedge`, and that is the only thing to read.
// CR-20260924-002: an ES-mode Adjust answer carries the gap the lens used per contract; every ES
// leg remembers its own, so a preview can create a missing ES silo on the same number (and refuse
// one built on another). SPX legs are left alone. No-op on every non-ES-mode answer.
function stampEsGaps(contents: SolutionContent[], gaps: EsContractGapDto[] | null): void {
    if (!gaps || gaps.length === 0) {
        return;
    }
    const byContract: { [contract: string]: number } = {};
    gaps.forEach(g => {
        if (g && g.contract && isValidNumber(g.gap)) {
            byContract[g.contract] = g.gap;
        }
    });
    const stamp = (leg: Leg) => {
        if (leg && leg.asset && byContract.hasOwnProperty(leg.asset)) {
            leg.esGap = byContract[leg.asset];
        }
    };
    contents.forEach(c => {
        (c.legs || []).forEach(stamp);
        (c.cards || []).forEach(card => (card.legs || []).forEach(stamp));
    });
}

function toPackageCard(c: CoPilotCardDto): PackageCard {
    return {
        title: c.title,
        legs: (c.legs || []).map(toLeg),
        targetHedgeId: c.targetHedgeId || undefined,
        targetOutcome: c.targetOutcome ? c.targetOutcome.map(toHolding) : undefined,
        closesTheHedge: c.closesTheHedge === true
    };
}

function toHolding(h: CoPilotHoldingDto): Holding {
    return {
        quantity: h.quantity,                         // SIGNED — a holding has no action to carry the side
        ticker: h.ticker,
        tickerDisplayName: h.tickerDisplayName
    };
}

function toLeg(l: CoPilotLegDto): Leg {
    const parsed = parseLegActionType(l.actionType);
    const ot = parseOptionTicker(l.ticker);
    return {
        quantity: l.quantity,
        ticker: l.ticker,
        tickerDisplayName: l.tickerDisplayName,
        action: l.actionType,
        side: parsed ? parsed.side : 'buy',
        effect: parsed ? parsed.effect : 'unknown',   // unparseable => rejected by applySolution, never mis-applied
        asset: ot ? ot.underlying : null,             // per-leg underlying for ES->SPX normFactor (skill path)
        // Stage 7: the existing hedge this leg trades on, decided by the BACKEND. Absent on a
        // To-Open leg. The field already existed for locally-generated close legs; this is the same
        // meaning arriving over the wire.
        groupId: l.groupId || undefined,
        // CR-20260918-001: the decomposed contract, read off the raw ticker (the engine speaks
        // ACTUAL strikes and ISO expirations). Without these the Transactions popup rendered every
        // Type / Expiration / Strike cell of an Adjust package blank.
        optionType: ot ? ot.type : null,
        strike: (ot && isValidNumber(ot.strike)) ? ot.strike : null,
        expiry: ot ? ot.expiration : null
    };
}
